import { type DragEvent, type FormEvent, type KeyboardEvent, type MouseEvent, useEffect, useMemo, useState } from "react";
import {
  archiveAdminCatalogProduct,
  createAdminCatalogProduct,
  downloadAdminCatalogStockExport,
  fetchAdminCatalog,
  fetchAdminCatalogProduct,
  type AdminCatalogCategory,
  type AdminCatalogItem,
  uploadAdminCatalogImage,
  updateAdminCatalogProduct,
  addAdminCatalogProductStock,
  fetchAdminCatalogProductStockLogs,
  type AdminCatalogStockLog
} from "../../lib/admin-catalog";
import { formatCurrency } from "../../lib/utils";

type EditorImage = {
  id: number | null;
  url: string;
};

type EditorState = {
  id: number | null;
  name: string;
  slug: string;
  salePrice: string;
  minimumSalePrice: string;
  quantity: string;
  description: string;
  testedWith: string;
  codeUrl: string;
  categoryId: string;
  sku: string;
  brand: string;
  specSummary: string;
  keywords: string;
  images: EditorImage[];
  active: boolean;
};

const emptyEditorState: EditorState = {
  id: null,
  name: "",
  slug: "",
  salePrice: "0",
  minimumSalePrice: "0",
  quantity: "0",
  description: "",
  testedWith: "",
  codeUrl: "",
  categoryId: "",
  sku: "",
  brand: "",
  specSummary: "",
  keywords: "",
  images: [],
  active: true
};

function slugify(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function toEditorState(item: AdminCatalogItem): EditorState {
  return {
    id: item.id,
    name: item.name,
    slug: item.slug,
    salePrice: String(item.price),
    minimumSalePrice: String(item.minimumPrice),
    quantity: String(item.quantity),
    description: item.description,
    testedWith: item.testedWith,
    codeUrl: item.codeUrl,
    categoryId: item.categoryId ? String(item.categoryId) : "",
    sku: item.sku,
    brand: item.brand,
    specSummary: item.specSummary,
    keywords: item.keywords,
    images: (item.images || (item.image ? [{ id: null, url: item.image }] : [])).map((image) => ({
      id: image.id ?? null,
      url: image.url
    })),
    active: item.active
  };
}

function ProductStateBadge({ active }: { active: boolean }) {
  return (
    <span
      className={`inline-flex whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold ${
        active
          ? "border border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/70 dark:bg-emerald-950/60 dark:text-emerald-300"
          : "border border-slate-300 bg-slate-100 text-slate-600 dark:border-slate-700 dark:bg-slate-900/80 dark:text-slate-300"
      }`}
    >
      {active ? "Active" : "Archived"}
    </span>
  );
}

function stopRowClick(event: MouseEvent<HTMLElement>) {
  event.stopPropagation();
}

function parseTagString(value: string) {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)
    .filter((item, index, items) => items.findIndex((entry) => entry.toLowerCase() === item.toLowerCase()) === index);
}

function joinTagString(tags: string[]) {
  return tags.join(", ");
}

function TokenField(props: {
  label: string;
  value: string;
  placeholder: string;
  onChange: (nextValue: string) => void;
}) {
  const tags = parseTagString(props.value);
  const [draft, setDraft] = useState("");

  function commitDraft() {
    const nextTags = parseTagString(joinTagString([...tags, draft]));
    if (nextTags.length !== tags.length) {
      props.onChange(joinTagString(nextTags));
    }
    setDraft("");
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      if (draft.trim()) {
        commitDraft();
      }
    } else if (event.key === "Backspace" && !draft && tags.length > 0) {
      event.preventDefault();
      props.onChange(joinTagString(tags.slice(0, -1)));
    }
  }

  function removeTag(tagToRemove: string) {
    props.onChange(joinTagString(tags.filter((tag) => tag !== tagToRemove)));
  }

  return (
    <div>
      <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">{props.label}</label>
      <div className="field-control flex min-h-[52px] flex-wrap items-center gap-2 px-3 py-2">
        {tags.map((tag) => (
          <span
            className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 dark:border-cyan-900/70 dark:bg-cyan-950/40 dark:text-cyan-300"
            key={tag}
          >
            <span>{tag}</span>
            <button
              className="rounded-full text-[10px] leading-none text-current opacity-70 transition hover:opacity-100"
              onClick={() => removeTag(tag)}
              type="button"
            >
              x
            </button>
          </span>
        ))}
        <input
          className="min-w-[180px] flex-1 bg-transparent py-1 text-sm outline-none placeholder:text-nv-slate"
          onBlur={() => {
            if (draft.trim()) {
              commitDraft();
            }
          }}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={tags.length === 0 ? props.placeholder : "Add another and press Enter or comma"}
          value={draft}
        />
      </div>
    </div>
  );
}

export function AdminCatalogPage() {
  const [items, setItems] = useState<AdminCatalogItem[]>([]);
  const [categories, setCategories] = useState<AdminCatalogCategory[]>([]);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"active" | "archived" | "all">("active");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 25,
    totalProducts: 0,
    totalPages: 1,
    hasPreviousPage: false,
    hasNextPage: false
  });
  const [summary, setSummary] = useState({
    totalProducts: 0,
    activeProducts: 0,
    archivedProducts: 0,
    lowStockProducts: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [loadingEditor, setLoadingEditor] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editor, setEditor] = useState<EditorState>(emptyEditorState);

  // Stock adjustment modal states
  const [stockModalOpen, setStockModalOpen] = useState(false);
  const [stockTargetProduct, setStockTargetProduct] = useState<AdminCatalogItem | null>(null);
  const [stockAdjustmentMode, setStockAdjustmentMode] = useState<"add" | "subtract" | "set">("add");
  const [stockQuantityInput, setStockQuantityInput] = useState("1");
  const [stockNote, setStockNote] = useState("");
  const [stockSaving, setStockSaving] = useState(false);

  // Stock history states
  const [stockHistory, setStockHistory] = useState<AdminCatalogStockLog[]>([]);
  const [stockHistoryLoading, setStockHistoryLoading] = useState(false);
  const [stockHistoryError, setStockHistoryError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  async function handleExportStock() {
    setExporting(true);
    try {
      await downloadAdminCatalogStockExport({
        query: query.trim() || undefined,
        active: statusFilter
      });
    } catch (exportErr) {
      alert(exportErr instanceof Error ? exportErr.message : "Failed to export stock to Excel.");
    } finally {
      setExporting(false);
    }
  }


  async function loadStockHistory(productId: number) {
    setStockHistoryLoading(true);
    setStockHistoryError(null);
    try {
      const history = await fetchAdminCatalogProductStockLogs(productId);
      setStockHistory(history);
    } catch (e) {
      setStockHistoryError(e instanceof Error ? e.message : "Unable to load stock history.");
    } finally {
      setStockHistoryLoading(false);
    }
  }

  async function handleAddStockSubmit(event: FormEvent) {
    event.preventDefault();
    if (!stockTargetProduct) return;
    
    const inputQty = Number(stockQuantityInput);
    if (Number.isNaN(inputQty) || inputQty < 0) {
      alert("Please enter a valid positive quantity.");
      return;
    }

    let qtyToAdd = 0;
    if (stockAdjustmentMode === "add") {
      qtyToAdd = inputQty;
    } else if (stockAdjustmentMode === "subtract") {
      qtyToAdd = -inputQty;
    } else if (stockAdjustmentMode === "set") {
      qtyToAdd = inputQty - stockTargetProduct.quantity;
    }

    if (qtyToAdd === 0) {
      alert("This adjustment results in no change to the current stock level.");
      return;
    }

    if (stockTargetProduct.quantity + qtyToAdd < 0) {
      alert("Resulting stock level cannot be less than 0.");
      return;
    }

    setStockSaving(true);
    try {
      await addAdminCatalogProductStock(stockTargetProduct.id, {
        quantity: qtyToAdd,
        note: stockNote.trim() || null
      });
      setStockModalOpen(false);
      setStockQuantityInput("1");
      setStockAdjustmentMode("add");
      setStockNote("");
      await reloadCurrentView();
      if (editor.id === stockTargetProduct.id) {
        const refreshed = await fetchAdminCatalogProduct(stockTargetProduct.id);
        setEditor(toEditorState(refreshed));
        void loadStockHistory(stockTargetProduct.id);
      }
    } catch (e) {
      alert(e instanceof Error ? e.message : "Failed to adjust stock.");
    } finally {
      setStockSaving(false);
    }
  }

  const editorPreviewSlug = useMemo(() => {
    return editor.slug.trim() || slugify(editor.name);
  }, [editor.name, editor.slug]);

  const metrics = useMemo(
    () => ({
      total: summary.totalProducts,
      active: summary.activeProducts,
      archived: summary.archivedProducts,
      lowStock: summary.lowStockProducts
    }),
    [summary]
  );

  useEffect(() => {
    let ignore = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();
        if (query.trim()) {
          params.set("q", query.trim());
        }
        params.set("active", statusFilter);
        params.set("page", String(page));
        params.set("pageSize", "25");

        const result = await fetchAdminCatalog(params);
        if (!ignore) {
          setItems(result.products);
          setCategories(result.categories);
          setPagination(result.pagination);
          setSummary(result.summary);
        }
      } catch (loadError) {
        if (!ignore) {
          setError(loadError instanceof Error ? loadError.message : "Unable to load catalog.");
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    void load();

    return () => {
      ignore = true;
    };
  }, [page, query, statusFilter]);

  useEffect(() => {
    setPage(1);
  }, [query, statusFilter]);

  function openCreate() {
    setEditor(emptyEditorState);
    setEditorOpen(true);
    setLoadingEditor(false);
    setError(null);
    setSuccessMessage(null);
    setStockHistory([]);
  }

  async function openEdit(item: AdminCatalogItem) {
    setEditorOpen(true);
    setLoadingEditor(true);
    setError(null);
    setSuccessMessage(null);
    setStockHistory([]);

    try {
      const detail = await fetchAdminCatalogProduct(item.id);
      setEditor(toEditorState(detail));
      void loadStockHistory(item.id);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Unable to load product details.");
    } finally {
      setLoadingEditor(false);
    }
  }

  async function reloadCurrentView() {
    const params = new URLSearchParams();
    if (query.trim()) {
      params.set("q", query.trim());
    }
    params.set("active", statusFilter);
    params.set("page", String(page));
    params.set("pageSize", "25");
    const result = await fetchAdminCatalog(params);
    setItems(result.products);
    setCategories(result.categories);
    setPagination(result.pagination);
    setSummary(result.summary);
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();

    if (!editor.name.trim()) {
      setError("Product name is required.");
      return;
    }

    if (!editor.description.trim()) {
      setError("Description is required.");
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const payload = {
        name: editor.name,
        slug: editor.slug,
        salePrice: Number(editor.salePrice || 0),
        minimumSalePrice: Number(editor.minimumSalePrice || 0),
        quantity: Number(editor.quantity || 0),
        description: editor.description,
        testedWith: editor.testedWith,
        codeUrl: editor.codeUrl,
        categoryId: editor.categoryId ? Number(editor.categoryId) : null,
        sku: editor.sku,
        brand: editor.brand,
        specSummary: editor.specSummary,
        keywords: editor.keywords,
        images: editor.images,
        active: editor.active
      };

      let savedProductId = editor.id;

      if (editor.id) {
        await updateAdminCatalogProduct(editor.id, payload);
      } else {
        const created = await createAdminCatalogProduct(payload);
        savedProductId = typeof created?.productId === "number" ? created.productId : null;
      }

      await reloadCurrentView();
      if (savedProductId) {
        const refreshed = await fetchAdminCatalogProduct(savedProductId);
        setEditor(toEditorState(refreshed));
      }
      setSuccessMessage(editor.id ? "Product changes saved successfully." : "Product created successfully.");
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Unable to save product.");
    } finally {
      setSaving(false);
    }
  }

  async function handleArchive(productId: number) {
    try {
      setError(null);
      setSuccessMessage(null);
      await archiveAdminCatalogProduct(productId);
      await reloadCurrentView();
    } catch (archiveError) {
      setError(archiveError instanceof Error ? archiveError.message : "Unable to archive product.");
    }
  }

  async function handleRestore(item: AdminCatalogItem) {
    try {
      setError(null);
      setSuccessMessage(null);
      await updateAdminCatalogProduct(item.id, {
        name: item.name,
        slug: item.slug,
        salePrice: item.price,
        minimumSalePrice: item.minimumPrice,
        quantity: item.quantity,
        description: item.description,
        testedWith: item.testedWith,
        codeUrl: item.codeUrl,
        categoryId: item.categoryId,
        sku: item.sku,
        brand: item.brand,
        specSummary: item.specSummary,
        keywords: item.keywords,
        images: (item.images || (item.image ? [{ id: null, url: item.image }] : [])).map((image) => ({
          id: image.id ?? null,
          url: image.url
        })),
        active: true
      });
      await reloadCurrentView();
    } catch (restoreError) {
      setError(restoreError instanceof Error ? restoreError.message : "Unable to restore product.");
    }
  }

  async function handleImageUpload(files: FileList | null) {
    if (!files || files.length === 0) {
      return;
    }

    setUploadingImage(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const uploaded = await Promise.all(Array.from(files).map((file) => uploadAdminCatalogImage(file)));
      setEditor((current) => ({
        ...current,
        images: [
          ...current.images,
          ...uploaded.map((result) => ({
            id: null,
            url: result.image.url
          }))
        ]
      }));
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : "Unable to upload image.");
    } finally {
      setUploadingImage(false);
    }
  }

  function handleDrop(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault();
    setDragActive(false);
    void handleImageUpload(event.dataTransfer.files);
  }

  function handleDragOver(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault();
    if (!dragActive) {
      setDragActive(true);
    }
  }

  function handleDragLeave(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault();
    setDragActive(false);
  }

  function removeEditorImage(index: number) {
    setEditor((current) => ({
      ...current,
      images: current.images.filter((_, imageIndex) => imageIndex !== index)
    }));
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">Catalog</p>
          <h1 className="mt-3 text-4xl font-semibold tracking-tight">Inventory and product control</h1>
          <p className="mt-4 max-w-3xl text-nv-slate">
            Create, update, merchandise, and archive products from one admin workspace built around the live catalog.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            className="inline-flex items-center gap-2 rounded-full border border-emerald-300/80 bg-emerald-50 px-5 py-3 text-sm font-semibold text-emerald-800 shadow-sm transition hover:bg-emerald-100 disabled:opacity-50 dark:border-emerald-700/60 dark:bg-emerald-950/60 dark:text-emerald-300 dark:hover:bg-emerald-900/60"
            disabled={exporting}
            onClick={() => void handleExportStock()}
            title="Export stock (Name, SKU, Qty, Price) to Excel / CSV"
            type="button"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            {exporting ? "Exporting..." : "Export to Excel"}
          </button>
          <button className="brand-button rounded-full px-5 py-3 text-sm font-semibold text-white" onClick={openCreate} type="button">
            New product
          </button>
        </div>
      </div>

      <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[minmax(0,1.5fr)_minmax(320px,1fr)]">
        <div className="min-h-0 space-y-4 overflow-y-auto pr-1">
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {[
              ["Visible now", metrics.active],
              ["Archived", metrics.archived],
              ["Low stock", metrics.lowStock],
              ["Catalog total", metrics.total]
            ].map(([label, value]) => (
              <div className="admin-card rounded-[28px] p-5" key={label}>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">{label}</p>
                <p className="mt-3 text-3xl font-semibold tracking-tight text-nv-ink">{value}</p>
              </div>
            ))}
          </div>

          <div className="admin-card rounded-[28px] p-5">
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_180px]">
              <input
                className="field-control px-4 py-3 text-sm"
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search by name, slug, brand, or SKU"
                value={query}
              />
              <select
                className="field-control px-4 py-3 text-sm"
                onChange={(event) => setStatusFilter(event.target.value as "active" | "archived" | "all")}
                value={statusFilter}
              >
                <option value="active">Active items</option>
                <option value="archived">Archived items</option>
                <option value="all">All items</option>
              </select>
            </div>
          </div>

          {error ? <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/70 dark:bg-red-950/40 dark:text-red-300">{error}</div> : null}

          <div className="admin-card overflow-hidden rounded-[32px]">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1040px]">
                <thead>
                  <tr className="border-b border-black/10 text-left text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">
                    <th className="px-5 py-4">Product</th>
                    <th className="px-5 py-4">Category</th>
                    <th className="px-5 py-4">Price</th>
                    <th className="px-5 py-4">Qty</th>
                    <th className="px-5 py-4">State</th>
                    <th className="px-5 py-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td className="px-5 py-8 text-sm text-nv-slate" colSpan={6}>
                        Loading catalog...
                      </td>
                    </tr>
                  ) : items.length > 0 ? (
                    items.map((item) => (
                      <tr
                        className="cursor-pointer border-b border-black/5 align-top transition-colors hover:bg-black/[0.03] dark:hover:bg-white/[0.03]"
                        key={item.id}
                        onClick={() => void openEdit(item)}
                      >
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-4">
                            <div className="h-16 w-16 shrink-0 overflow-hidden rounded-2xl border border-black/10 bg-black/5 dark:border-white/10 dark:bg-white/5">
                              {item.image ? <img alt={item.name} className="h-full w-full object-cover" src={item.image} /> : null}
                            </div>
                            <div className="min-w-0">
                              <p className="truncate font-semibold text-nv-ink">{item.name}</p>
                              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-nv-slate">
                                <span>#{item.id}</span>
                                {item.sku ? <span>SKU {item.sku}</span> : null}
                                {item.brand ? <span>{item.brand}</span> : null}
                              </div>
                              <p className="mt-1 truncate text-xs text-nv-slate">/{item.slug}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4 text-sm text-nv-slate">{item.category}</td>
                        <td className="px-5 py-4">
                          <p className="text-sm font-semibold text-nv-ink">{formatCurrency(item.price)}</p>
                          {item.minimumPrice > 0 ? <p className="mt-1 text-xs text-nv-slate">Floor {formatCurrency(item.minimumPrice)}</p> : null}
                        </td>
                        <td className="px-5 py-4">
                          <p className="text-sm font-semibold text-nv-ink">{item.quantity}</p>
                          {item.quantity <= 5 && item.active ? <p className="mt-1 text-xs text-amber-600 dark:text-amber-300">Needs restock</p> : null}
                        </td>
                        <td className="px-5 py-4">
                          <ProductStateBadge active={item.active} />
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex flex-wrap items-center gap-2">
                            <a
                              className="rounded-full border border-black/10 px-4 py-2 text-sm font-semibold dark:border-white/10"
                              href={`/shop/product/${item.slug}/${item.id}`}
                              onClick={stopRowClick}
                              rel="noreferrer"
                              target="_blank"
                            >
                              Open
                            </a>
                            <button
                              className="rounded-full border border-black/10 px-4 py-2 text-sm font-semibold dark:border-white/10"
                              onClick={(event) => {
                                stopRowClick(event);
                                void openEdit(item);
                              }}
                              type="button"
                            >
                              Edit
                            </button>
                            <button
                              className="rounded-full border border-cyan-200 bg-cyan-50 px-4 py-2 text-sm font-semibold text-cyan-700 dark:border-cyan-900/70 dark:bg-cyan-950/40 dark:text-cyan-300"
                              onClick={(event) => {
                                stopRowClick(event);
                                setStockTargetProduct(item);
                                setStockAdjustmentMode("add");
                                setStockQuantityInput("1");
                                setStockModalOpen(true);
                              }}
                              type="button"
                            >
                              Adjust Stock
                            </button>
                            {item.active ? (
                              <button
                                className="rounded-full border border-red-200 px-4 py-2 text-sm font-semibold text-red-700 dark:border-red-900/70 dark:text-red-300"
                                onClick={(event) => {
                                  stopRowClick(event);
                                  void handleArchive(item.id);
                                }}
                                type="button"
                              >
                                Archive
                              </button>
                            ) : (
                              <button
                                className="rounded-full border border-emerald-200 px-4 py-2 text-sm font-semibold text-emerald-700 dark:border-emerald-900/70 dark:text-emerald-300"
                                onClick={(event) => {
                                  stopRowClick(event);
                                  void handleRestore(item);
                                }}
                                type="button"
                              >
                                Restore
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="px-5 py-8 text-sm text-nv-slate" colSpan={6}>
                        No catalog items matched the current view.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-black/5 px-5 py-4 text-sm text-nv-slate dark:border-white/10">
              <p>
                Page {pagination.page} of {pagination.totalPages} · {pagination.totalProducts} items
              </p>
              <div className="flex items-center gap-2">
                <button
                  className="rounded-full border border-black/10 px-4 py-2 font-semibold disabled:opacity-40 dark:border-white/10"
                  disabled={!pagination.hasPreviousPage || loading}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                  type="button"
                >
                  Previous
                </button>
                <button
                  className="rounded-full border border-black/10 px-4 py-2 font-semibold disabled:opacity-40 dark:border-white/10"
                  disabled={!pagination.hasNextPage || loading}
                  onClick={() => setPage((current) => current + 1)}
                  type="button"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="min-h-0 overflow-y-auto pr-1">
          <div className="admin-card rounded-[32px] p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">{editor.id ? "Edit product" : "Create product"}</p>
                <h2 className="mt-2 text-2xl font-semibold tracking-tight text-nv-ink">
                  {editor.id ? editor.name || "Untitled product" : "New catalog item"}
                </h2>
              </div>
              {editorOpen ? (
                <button className="rounded-full border border-black/10 px-4 py-2 text-sm font-semibold dark:border-white/10" onClick={() => setEditorOpen(false)} type="button">
                  Close
                </button>
              ) : null}
            </div>

            {!editorOpen ? (
              <div className="mt-6 rounded-3xl border border-dashed border-black/10 p-5 text-sm text-nv-slate dark:border-white/10">
                Pick a product from the table to edit it, or create a new catalog item.
              </div>
            ) : loadingEditor ? (
              <div className="mt-6 rounded-3xl border border-dashed border-black/10 p-5 text-sm text-nv-slate dark:border-white/10">
                Loading product details...
              </div>
            ) : (
              <form className="mt-6 space-y-5" onSubmit={(event) => void handleSubmit(event)}>
                {successMessage ? (
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700 dark:border-emerald-900/70 dark:bg-emerald-950/40 dark:text-emerald-300">
                    {successMessage}
                  </div>
                ) : null}
                <div className="grid gap-5 md:grid-cols-2">
                  <div className="md:col-span-2">
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Product name</label>
                    <input
                      className="field-control px-4 py-3 text-sm"
                      onChange={(event) => setEditor((current) => ({ ...current, name: event.target.value }))}
                      required
                      value={editor.name}
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Slug</label>
                    <input className="field-control px-4 py-3 text-sm" onChange={(event) => setEditor((current) => ({ ...current, slug: event.target.value }))} value={editor.slug} />
                    <p className="mt-2 text-xs text-nv-slate">Preview: /shop/product/{editorPreviewSlug || "product-name"}</p>
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Category</label>
                    <select className="field-control px-4 py-3 text-sm" onChange={(event) => setEditor((current) => ({ ...current, categoryId: event.target.value }))} value={editor.categoryId}>
                      <option value="">No category</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Sale price</label>
                    <input
                      className="field-control px-4 py-3 text-sm"
                      inputMode="decimal"
                      onChange={(event) => setEditor((current) => ({ ...current, salePrice: event.target.value }))}
                      required
                      value={editor.salePrice}
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Minimum price</label>
                    <input
                      className="field-control px-4 py-3 text-sm"
                      inputMode="decimal"
                      onChange={(event) => setEditor((current) => ({ ...current, minimumSalePrice: event.target.value }))}
                      required
                      value={editor.minimumSalePrice}
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Quantity</label>
                    <div className="flex gap-2">
                      <input
                        className={`field-control px-4 py-3 text-sm flex-1 ${
                          editor.id ? "bg-black/5 dark:bg-white/5 cursor-not-allowed opacity-80" : ""
                        }`}
                        inputMode="numeric"
                        onChange={(event) => setEditor((current) => ({ ...current, quantity: event.target.value }))}
                        required
                        readOnly={Boolean(editor.id)}
                        value={editor.quantity}
                      />
                      {editor.id ? (
                        <button
                          className="brand-button rounded-2xl px-4 py-3 text-sm font-semibold text-white"
                          onClick={() => {
                            const matched = items.find((item) => item.id === editor.id);
                            if (matched) {
                              setStockTargetProduct(matched);
                              setStockAdjustmentMode("add");
                              setStockQuantityInput("1");
                              setStockModalOpen(true);
                            }
                          }}
                          type="button"
                        >
                          Adjust
                        </button>
                      ) : null}
                    </div>
                    {editor.id ? <p className="mt-1 text-xs text-nv-slate">Quantity is read-only. Click "Adjust" to modify stock levels.</p> : null}
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">SKU</label>
                    <input className="field-control px-4 py-3 text-sm" onChange={(event) => setEditor((current) => ({ ...current, sku: event.target.value }))} value={editor.sku} />
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Brand</label>
                    <input className="field-control px-4 py-3 text-sm" onChange={(event) => setEditor((current) => ({ ...current, brand: event.target.value }))} value={editor.brand} />
                  </div>
                  <div className="md:col-span-2">
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <label className="block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Product images</label>
                      <p className="text-xs text-nv-slate">First image is used as the primary storefront image.</p>
                    </div>
                    <label
                      className={`mt-3 flex min-h-36 cursor-pointer flex-col items-center justify-center rounded-[28px] border border-dashed px-6 py-6 text-center transition ${
                        dragActive
                          ? "border-blue-500 bg-blue-50/80 dark:border-cyan-400 dark:bg-cyan-500/10"
                          : "border-black/10 bg-black/[0.02] hover:bg-black/[0.03] dark:border-white/10 dark:bg-white/[0.03] dark:hover:bg-white/[0.05]"
                      }`}
                      onDragLeave={handleDragLeave}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                    >
                      <div className="flex h-12 w-12 items-center justify-center rounded-full border border-black/10 text-xl text-nv-slate dark:border-white/10">
                        +
                      </div>
                      <p className="mt-4 text-sm font-semibold text-nv-ink">
                        {uploadingImage ? "Uploading images..." : "Drag and drop images here"}
                      </p>
                      <p className="mt-2 text-sm text-nv-slate">or click to choose one or more files</p>
                      <p className="mt-1 text-xs text-nv-slate">PNG, JPG, WEBP and other image formats are supported.</p>
                      <input
                        accept="image/*"
                        className="hidden"
                        multiple
                        disabled={uploadingImage}
                        onChange={(event) => {
                          void handleImageUpload(event.target.files);
                          event.target.value = "";
                        }}
                        type="file"
                      />
                    </label>
                    {editor.images.length > 0 ? (
                      <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                        {editor.images.map((image, index) => (
                          <div className="rounded-3xl border border-black/10 bg-black/[0.02] p-3 dark:border-white/10 dark:bg-white/[0.03]" key={`${image.id ?? "new"}-${image.url}-${index}`}>
                            <div className="overflow-hidden rounded-2xl">
                              <img alt={editor.name || `Product image ${index + 1}`} className="h-36 w-full object-cover" src={image.url} />
                            </div>
                            <div className="mt-3 flex items-center justify-between gap-3">
                              <p className="min-w-0 truncate text-xs text-nv-slate">{index === 0 ? "Primary image" : `Gallery image ${index + 1}`}</p>
                              <button
                                className="rounded-full border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-700 dark:border-red-900/70 dark:text-red-300"
                                onClick={() => removeEditorImage(index)}
                                type="button"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="mt-4 rounded-3xl border border-dashed border-black/10 p-4 text-sm text-nv-slate dark:border-white/10">
                        No product images yet. Upload images to build the gallery.
                      </div>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Description</label>
                    <textarea
                      className="field-control min-h-[140px] px-4 py-3 text-sm"
                      onChange={(event) => setEditor((current) => ({ ...current, description: event.target.value }))}
                      required
                      value={editor.description}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <TokenField
                      label="Tested with"
                      onChange={(nextValue) => setEditor((current) => ({ ...current, testedWith: nextValue }))}
                      placeholder="Type a platform, then press Enter or comma"
                      value={editor.testedWith}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Code URL</label>
                    <input className="field-control px-4 py-3 text-sm" onChange={(event) => setEditor((current) => ({ ...current, codeUrl: event.target.value }))} value={editor.codeUrl} />
                  </div>
                  <div className="md:col-span-2">
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Spec summary</label>
                    <textarea className="field-control min-h-[120px] px-4 py-3 text-sm" onChange={(event) => setEditor((current) => ({ ...current, specSummary: event.target.value }))} value={editor.specSummary} />
                  </div>
                  <div className="md:col-span-2">
                    <TokenField
                      label="Keywords"
                      onChange={(nextValue) => setEditor((current) => ({ ...current, keywords: nextValue }))}
                      placeholder="Type a keyword, then press Enter or comma"
                      value={editor.keywords}
                    />
                  </div>
                </div>

                {editor.id ? (
                  <div className="space-y-3 pt-6 border-t border-black/10 dark:border-white/10">
                    <label className="block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Stock Addition History</label>
                    {stockHistoryLoading ? (
                      <p className="text-xs text-nv-slate">Loading history...</p>
                    ) : stockHistoryError ? (
                      <p className="text-xs text-red-500">{stockHistoryError}</p>
                    ) : stockHistory.length > 0 ? (
                      <div className="overflow-x-auto rounded-2xl border border-black/5 dark:border-white/10">
                        <table className="w-full text-left text-xs">
                          <thead>
                            <tr className="bg-black/[0.02] dark:bg-white/[0.02] text-nv-slate border-b border-black/5 dark:border-white/5">
                              <th className="px-3 py-2">Date</th>
                              <th className="px-3 py-2">Added By</th>
                              <th className="px-3 py-2 text-right">Added</th>
                              <th className="px-3 py-2 text-right">Result</th>
                              <th className="px-3 py-2">Note</th>
                            </tr>
                          </thead>
                          <tbody>
                            {stockHistory.map((log) => (
                              <tr className="border-b border-black/5 dark:border-white/5 last:border-0" key={log.id}>
                                <td className="px-3 py-2 whitespace-nowrap text-nv-slate">
                                  {new Date(log.addedOn).toLocaleString()}
                                </td>
                                <td className="px-3 py-2 font-medium">{log.addedBy}</td>
                                <td className={`px-3 py-2 text-right font-semibold ${
                                  log.addedQuantity > 0 ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"
                                }`}>
                                  {log.addedQuantity > 0 ? `+${log.addedQuantity}` : log.addedQuantity}
                                </td>
                                <td className="px-3 py-2 text-right font-semibold">{log.newQuantity}</td>
                                <td className="px-3 py-2 text-nv-slate italic max-w-[200px] truncate" title={log.note}>
                                  {log.note || "—"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-xs text-nv-slate italic">No manual stock additions logged for this product.</p>
                    )}
                  </div>
                ) : null}

                {editor.id ? (
                  <label className="field-soft flex items-center gap-3 px-4 py-3 text-sm">
                    <input checked={editor.active} onChange={(event) => setEditor((current) => ({ ...current, active: event.target.checked }))} type="checkbox" />
                    Product is active
                  </label>
                ) : null}

                <div className="flex items-center gap-3">
                  <button className="brand-button rounded-full px-5 py-3 text-sm font-semibold text-white disabled:opacity-60" disabled={saving} type="submit">
                    {saving ? "Saving..." : editor.id ? "Save changes" : "Create product"}
                  </button>
                  <button className="rounded-full border border-black/10 px-5 py-3 text-sm font-semibold dark:border-white/10" onClick={() => setEditorOpen(false)} type="button">
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      {stockModalOpen && stockTargetProduct ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm animate-fade-in">
          <div className="admin-card w-full max-w-md overflow-hidden rounded-[32px] bg-white p-6 shadow-2xl dark:bg-nv-field-bg animate-scale-in">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-cyan-100 text-cyan-600 dark:bg-cyan-950/50 dark:text-cyan-400">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
              </div>
              <div className="space-y-2 flex-1 min-w-0">
                <h3 className="text-xl font-semibold tracking-tight text-nv-ink">Adjust Stock</h3>
                <p className="text-sm font-medium text-nv-ink truncate">{stockTargetProduct.name}</p>
                <p className="text-xs text-nv-slate">
                  Current Stock Level: <span className="font-semibold text-nv-ink">{stockTargetProduct.quantity}</span>
                </p>
              </div>
            </div>

            <form onSubmit={(event) => void handleAddStockSubmit(event)} className="mt-6 space-y-4">
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Action</label>
                <div className="flex flex-wrap gap-3">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="radio"
                      name="adjustmentMode"
                      checked={stockAdjustmentMode === "add"}
                      onChange={() => setStockAdjustmentMode("add")}
                    />
                    Add
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="radio"
                      name="adjustmentMode"
                      checked={stockAdjustmentMode === "subtract"}
                      onChange={() => setStockAdjustmentMode("subtract")}
                    />
                    Subtract
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="radio"
                      name="adjustmentMode"
                      checked={stockAdjustmentMode === "set"}
                      onChange={() => setStockAdjustmentMode("set")}
                    />
                    Set Absolute
                  </label>
                </div>
              </div>

              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">
                  {stockAdjustmentMode === "set" ? "New Total Quantity" : "Quantity"}
                </label>
                <input
                  className="field-control px-4 py-3 text-sm"
                  inputMode="numeric"
                  min={stockAdjustmentMode === "set" ? "0" : "1"}
                  required
                  type="number"
                  onChange={(event) => setStockQuantityInput(event.target.value)}
                  value={stockQuantityInput}
                />
              </div>

              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Reason / Note (Optional)</label>
                <input
                  className="field-control px-4 py-3 text-sm"
                  placeholder="e.g. Received shipment from supplier"
                  type="text"
                  onChange={(event) => setStockNote(event.target.value)}
                  value={stockNote}
                />
              </div>

              <div className="mt-6 flex justify-end gap-3 border-t border-black/5 pt-4 dark:border-white/10">
                <button
                  className="rounded-full border border-black/10 px-5 py-2.5 text-sm font-semibold hover:bg-black/5 disabled:opacity-40 dark:border-white/10 dark:hover:bg-white/5"
                  disabled={stockSaving}
                  onClick={() => setStockModalOpen(false)}
                  type="button"
                >
                  Cancel
                </button>
                <button
                  className="brand-button rounded-full px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-40"
                  disabled={stockSaving}
                  type="submit"
                >
                  {stockSaving ? "Saving..." : "Confirm Adjustment"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
